import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    type ActorEvent,
    type ButtonItem,
    EVENT_TYPE,
    type Field,
    type FieldADTValue,
    type RecordLikeProtocol,
    type RichTextContentProtocol,
    type ViewField
} from '@lighthouse/core'
import { fieldConvertValue, useActionRunningLoadings, ViewFieldValuePreviewer } from '@lighthouse/shared'
import type { FC, HTMLAttributes } from 'react'
import React, { forwardRef, memo, useCallback, useMemo } from 'react'

import { RecordOperator } from '../../components/RecordOperator'
import { useViewBlockContext } from '../../context'
import * as SC from './styles'

export interface RecordRenderProps {
    blockId: string
    schema: Record<string, Field>
    record: RecordLikeProtocol
    columns: ViewField[]
    disabled?: boolean
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

interface RecordItemProps extends RecordRenderProps, HTMLAttributes<HTMLDivElement> {}

interface SortableRecordItemProps extends RecordItemProps {
    id: string
}

export const RecordItem = forwardRef<HTMLDivElement, RecordItemProps>((props, ref) => {
    const { blockId, record, schema, columns, disabled, onRecordOperatorActionTrigger, onRecordClick, onRenderButtonTitle, ...rest } = props
    const { id: recordId, dsId, content } = record
    const { uniqueId, blockData } = useViewBlockContext()
    const { config } = blockData
    const { recordOperator, eventList } = config
    const titleFieldId = columns[0].fieldId
    const titleField = schema[titleFieldId]
    const recordClickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.RECORD_CLICK), [eventList])
    const clickable = !!recordClickEvent || !!onRecordClick
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()

    const handleRecordClick = useCallback(() => {
        if (!onRecordClick || !recordClickEvent) {
            return
        }
        handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent: recordClickEvent,
            trigger: (id, event) => onRecordClick(id, event, record)
        })
    }, [handleActionTriggerWithLoading, onRecordClick, record, recordClickEvent, uniqueId])

    const handleActionTrigger = useCallback(
        async (id: string, actorEvent: ActorEvent) => {
            await onRecordOperatorActionTrigger?.(id, actorEvent, record)
        },
        [onRecordOperatorActionTrigger, record]
    )

    const handleRenderButtonTitle = useCallback(
        (v: RichTextContentProtocol) => {
            return onRenderButtonTitle(v, record)
        },
        [onRenderButtonTitle, record]
    )

    return (
        <SC.RecordItemWrapper>
            <SC.OperatorContainer>
                <RecordOperator
                    uniqueId={uniqueId}
                    actions={recordOperator}
                    recordId={recordId}
                    record={record}
                    onActionTrigger={handleActionTrigger}
                    onRenderButtonTitle={handleRenderButtonTitle}
                />
            </SC.OperatorContainer>
            {/* 2023-09-06 14:15:01 灿白说去掉，后面只有表格和高级表格有 CheckBox */}
            <SC.RecordItemContainer
                ref={ref}
                {...rest}
                style={{ cursor: loadings[recordId] ? 'wait' : 'pointer' }}
                data-record-id={recordId}
                onClick={handleRecordClick}
                hasHoverStyle={clickable || !disabled}
            >
                {titleField && (
                    <ViewFieldValuePreviewer isBold enableEdit={false} field={titleField} disabled data={content?.[titleField.id]?.value} />
                )}
                {columns.slice(1).map(col => {
                    const field = schema[col.fieldId]
                    const data = content?.[col.fieldId]?.value
                    const fieldValue = { ...field, value: data } as FieldADTValue
                    const realValue = fieldConvertValue(fieldValue)
                    const isRender = !!field && !!realValue
                    if (!col.visible || !isRender) {
                        return null
                    }
                    return (
                        <SC.RecordItemField key={col.fieldId}>
                            <SC.RecordItemFieldHead>
                                {/* <IconFont type={getFieldIcon(col.fieldId, col.type)} /> */}
                                <div>{col.title}</div>
                            </SC.RecordItemFieldHead>
                            <ViewFieldValuePreviewer enableEdit={false} field={field} data={data} disabled />
                        </SC.RecordItemField>
                    )
                })}
            </SC.RecordItemContainer>
        </SC.RecordItemWrapper>
    )
})

export const SortableRecordItem: FC<SortableRecordItemProps> = memo(({ disabled, id, ...rest }) => {
    const { setNodeRef, listeners, attributes, isDragging, transition, transform } = useSortable({
        id,
        disabled
    })

    return (
        <RecordItem
            ref={disabled ? undefined : setNodeRef}
            style={{ transition, transform: CSS.Transform.toString(transform), opacity: isDragging ? 0.5 : undefined }}
            {...attributes}
            {...listeners}
            {...rest}
            disabled={disabled}
        />
    )
})
